import { useState } from 'react';
import { Button, Alert, Stepper, ConfirmDialog, ProgressBar } from '@dreamtree-org/twreact-ui';
import { SectionCard, KeyValue, StatusBadge } from '@/components';
import { formatDateTime } from '../../lib/format';

/** Tenancy external-mode steps, in order (ADR 0001 §5). */
const STEPS = [
  { key: 'url_encrypted', title: 'URL encrypted', description: 'Envelope stored' },
  { key: 'schema_applied', title: 'Schema applied', description: 'KORM sync' },
  { key: 'seeded', title: 'Seeded', description: 'RBAC + admin' },
  { key: 'verified', title: 'Verified', description: 'Stamp checked' },
];

const stepIndex = (job) => {
  if (!job) return 0;
  if (job.status === 'succeeded' || job.step === 'done') return STEPS.length;
  const i = STEPS.findIndex((s) => s.key === job.step);
  return i < 0 ? 0 : i;
};

/** Provisioning job (REAL) with retry for a failed merchant. */
export default function ProvisioningTab({ merchant: m, act, busy, canManage }) {
  const job = m.provisioning;
  const [confirm, setConfirm] = useState(false);
  const canRetry = m.status === 'provisioning_failed' || (job && job.status === 'failed');

  if (!job) return <SectionCard title="Provisioning"><p className="text-sm opacity-70">No provisioning job recorded for this merchant.</p></SectionCard>;

  return (
    <div className="space-y-4">
      <SectionCard
        title="Provisioning job"
        actions={canRetry && canManage ? <Button loading={busy === 'retry'} onClick={() => setConfirm(true)}>Retry provisioning</Button> : <StatusBadge status={job.status} />}
      >
        <div className="overflow-x-auto">
          <Stepper steps={STEPS} currentStep={stepIndex(job)} orientation="horizontal" />
        </div>
        <KeyValue columns={2} items={[
          { label: 'Job id', value: <code className="text-xs break-all">{job.id}</code> },
          { label: 'Status', value: <StatusBadge status={job.status} /> },
          { label: 'Current step', value: job.step },
          { label: 'Attempts', value: `${job.attempts} / ${job.max_attempts}` },
          { label: 'Created', value: formatDateTime(job.created_at) },
          { label: 'Updated', value: formatDateTime(job.updated_at) },
        ]} />
        <div className="space-y-1">
          <div className="text-xs opacity-60">Retry budget used</div>
          <ProgressBar value={job.attempts} max={job.max_attempts} size="sm" ariaLabel="Attempts used" />
        </div>
        {job.last_error && job.status !== 'succeeded' ? (
          <Alert variant="error" title="Last error"><span className="text-xs break-all">{job.last_error}</span></Alert>
        ) : null}
        <p className="text-xs opacity-60">A merchant becomes active only when every step succeeds; a failed run leaves it offline and retryable (idempotent per step).</p>
      </SectionCard>
      {confirm ? (
        <ConfirmDialog
          isOpen
          title={`Retry provisioning for ${m.display_name}?`}
          description="Resumes the job from its last completed step."
          confirmLabel="Retry provisioning"
          loading={busy === 'retry'}
          onCancel={() => setConfirm(false)}
          onConfirm={async () => {
            await act('retry', 'platformRetry');
            setConfirm(false);
          }}
        />
      ) : null}
    </div>
  );
}
